import { useState } from "react";

import { AffectedMemberType } from "@/blocks/UpdateHouseholdAddressesDialog";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import {
  AddressUpdateType,
  HouseholdMemberAddressFormType,
  HouseholdMemberType,
  HouseholdMemberUpdateType,
} from "@/domain/householdMember";
import { formatAddress } from "@/utils/address";

// The primary applicant's own save is deferred until the dialog resolves, so their
// address and the matching members' are applied from a single decision.
export const useUpdateHouseholdAddressesDialog = () => {
  const { mutate: updateHouseholdMember } = useUpdateHouseholdMemberMutation();
  const { data: householdMembers } = useListHouseholdMembersQuery();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [affectedMembers, setAffectedMembers] = useState<AffectedMemberType[]>(
    [],
  );
  const [addressType, setAddressType] =
    useState<AddressUpdateType>("homeAndMailing");
  const [primaryApplicantPendingUpdate, setPrimaryApplicantPendingUpdate] =
    useState<{
      id: string;
      data: HouseholdMemberUpdateType;
      onDone: () => void;
    } | null>(null);

  const handlePrimaryApplicantSaveSuccess = ({
    savedFormData,
    oldMemberData: oldPrimaryMemberData,
    onDone,
  }: {
    savedFormData: HouseholdMemberAddressFormType;
    oldMemberData: HouseholdMemberType;
    onDone: () => void;
  }) => {
    const paUpdateData: HouseholdMemberUpdateType = {
      mainAddressStreetOne: savedFormData.mainAddressStreetOne,
      mainAddressStreetTwo: savedFormData.mainAddressStreetTwo,
      mainAddressComplement: savedFormData.mainAddressComplement,
      mainAddressCity: savedFormData.mainAddressCity,
      mainAddressState: savedFormData.mainAddressState,
      mainAddressZip: savedFormData.mainAddressZip,
      mainCounty: savedFormData.mainCounty,
      mailingAddressStreetOne: savedFormData.mailingAddressStreetOne,
      mailingAddressStreetTwo: savedFormData.mailingAddressStreetTwo,
      mailingAddressComplement: savedFormData.mailingAddressComplement,
      mailingAddressCity: savedFormData.mailingAddressCity,
      mailingAddressState: savedFormData.mailingAddressState,
      mailingAddressZip: savedFormData.mailingAddressZip,
      mailingCounty: savedFormData.mailingCounty,
      isHomeless: savedFormData.isHomeless,
      hasNoMailingAddress: savedFormData.hasNoMailingAddress,
    };

    if (!householdMembers) {
      updateHouseholdMember(
        { id: oldPrimaryMemberData.id, householdMemberData: paUpdateData },
        { onSuccess: () => onDone() },
      );
      return;
    }

    const oldPrimaryMainAddress = formatAddress({
      streetOne: oldPrimaryMemberData.mainAddressStreetOne,
      streetTwo: oldPrimaryMemberData.mainAddressStreetTwo,
      city: oldPrimaryMemberData.mainAddressCity,
      state: oldPrimaryMemberData.mainAddressState,
      zip: oldPrimaryMemberData.mainAddressZip,
    });

    const oldPrimaryMailingAddress = formatAddress({
      streetOne: oldPrimaryMemberData.mailingAddressStreetOne,
      streetTwo: oldPrimaryMemberData.mailingAddressStreetTwo,
      city: oldPrimaryMemberData.mailingAddressCity,
      state: oldPrimaryMemberData.mailingAddressState,
      zip: oldPrimaryMemberData.mailingAddressZip,
    });

    const newPrimaryMainAddress = formatAddress({
      streetOne: savedFormData.mainAddressStreetOne,
      streetTwo: savedFormData.mainAddressStreetTwo,
      city: savedFormData.mainAddressCity,
      state: savedFormData.mainAddressState,
      zip: savedFormData.mainAddressZip,
    });

    const newPrimaryMailingAddress = formatAddress({
      streetOne: savedFormData.mailingAddressStreetOne,
      streetTwo: savedFormData.mailingAddressStreetTwo,
      city: savedFormData.mailingAddressCity,
      state: savedFormData.mailingAddressState,
      zip: savedFormData.mailingAddressZip,
    });

    const isMainAddressChanged =
      oldPrimaryMainAddress !== newPrimaryMainAddress;
    const isMailingAddressChanged =
      oldPrimaryMailingAddress !== newPrimaryMailingAddress;

    const nonPrimaryMembers = householdMembers.filter(
      m => m.id !== oldPrimaryMemberData.id,
    );

    const affected: AffectedMemberType[] = nonPrimaryMembers
      .map(m => {
        const memberMain = formatAddress({
          streetOne: m.mainAddressStreetOne,
          streetTwo: m.mainAddressStreetTwo,
          city: m.mainAddressCity,
          state: m.mainAddressState,
          zip: m.mainAddressZip,
        });
        const memberMailing = formatAddress({
          streetOne: m.mailingAddressStreetOne,
          streetTwo: m.mailingAddressStreetTwo,
          city: m.mailingAddressCity,
          state: m.mailingAddressState,
          zip: m.mailingAddressZip,
        });

        const isSharesMainAddress =
          isMainAddressChanged &&
          Boolean(oldPrimaryMainAddress) &&
          memberMain === oldPrimaryMainAddress;
        const isSharesMailingAddress =
          isMailingAddressChanged &&
          Boolean(oldPrimaryMailingAddress) &&
          memberMailing === oldPrimaryMailingAddress;

        if (!isSharesMainAddress && !isSharesMailingAddress) return null;

        return {
          member: m,
          addressUpdate: {
            ...(isSharesMainAddress
              ? {
                  mainAddressStreetOne: savedFormData.mainAddressStreetOne,
                  mainAddressStreetTwo: savedFormData.mainAddressStreetTwo,
                  mainAddressComplement: savedFormData.mainAddressComplement,
                  mainAddressCity: savedFormData.mainAddressCity,
                  mainAddressState: savedFormData.mainAddressState,
                  mainAddressZip: savedFormData.mainAddressZip,
                  mainCounty: savedFormData.mainCounty,
                  isHomeless: savedFormData.isHomeless,
                }
              : {}),
            ...(isSharesMailingAddress
              ? {
                  mailingAddressStreetOne:
                    savedFormData.mailingAddressStreetOne,
                  mailingAddressStreetTwo:
                    savedFormData.mailingAddressStreetTwo,
                  mailingAddressComplement:
                    savedFormData.mailingAddressComplement,
                  mailingAddressCity: savedFormData.mailingAddressCity,
                  mailingAddressState: savedFormData.mailingAddressState,
                  mailingAddressZip: savedFormData.mailingAddressZip,
                  mailingCounty: savedFormData.mailingCounty,
                  hasNoMailingAddress: savedFormData.hasNoMailingAddress,
                }
              : {}),
          },
        };
      })
      .filter(Boolean) as AffectedMemberType[];

    if (affected.length > 0) {
      const hasMain = affected.some(
        a => a.addressUpdate.mainAddressStreetOne !== undefined,
      );
      const hasMailing = affected.some(
        a => a.addressUpdate.mailingAddressStreetOne !== undefined,
      );
      const computedAddressType: AddressUpdateType =
        hasMain && hasMailing ? "homeAndMailing" : hasMain ? "home" : "mailing";

      setPrimaryApplicantPendingUpdate({
        id: oldPrimaryMemberData.id,
        data: paUpdateData,
        onDone,
      });
      setAffectedMembers(affected);
      setAddressType(computedAddressType);
      setIsDialogOpen(true);
    } else {
      updateHouseholdMember(
        { id: oldPrimaryMemberData.id, householdMemberData: paUpdateData },
        { onSuccess: () => onDone() },
      );
    }
  };

  const dialogProps = {
    open: isDialogOpen,
    onOpenChange: (open: boolean) => {
      setIsDialogOpen(open);
      if (!open) {
        setPrimaryApplicantPendingUpdate(null);
        setAffectedMembers([]);
      }
    },
    affectedMembers,
    addressType,
    primaryApplicantId: primaryApplicantPendingUpdate?.id ?? "",
    primaryApplicantUpdate: primaryApplicantPendingUpdate?.data ?? {},
    onCompleted: () => {
      primaryApplicantPendingUpdate?.onDone();
      setPrimaryApplicantPendingUpdate(null);
      setAffectedMembers([]);
    },
  };

  return { handlePrimaryApplicantSaveSuccess, dialogProps };
};
